/* crm-forecasting.jsx — Revenue Forecasting view for Phase 3
   Provides: RevenueForecastView
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const fmtMoney = (n) => '$' + (n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : Math.round(n || 0));
const fmtMoneyFull = (n) => '$' + Number(Math.round(n || 0)).toLocaleString();

/* ── Stat Card ── */
const ForecastStatCard = ({ label, value, sub, tone }) => {
  const colors = { blue: 'var(--nx-accent)', green: 'var(--nx-success)', amber: '#F59E0B', purple: '#8B5CF6' };
  const bg     = { blue: '#EDF4FD', green: '#E7F5EE', amber: '#FEF6E6', purple: '#F3EDFB' };
  const c = colors[tone] || 'var(--nx-accent)';
  const b = bg[tone] || '#EDF4FD';
  return (
    <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: c, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
};

/* ── Simple SVG bar chart ── */
const BarChart = ({ data, height = 140 }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barW   = Math.floor(280 / data.length) - 8;
  return (
    <svg width="100%" viewBox={`0 0 300 ${height + 36}`} style={{ overflow: 'visible' }}>
      {data.map((d, i) => {
        const barH  = Math.max(2, Math.round((d.value / maxVal) * height));
        const x     = i * (barW + 8) + 4;
        const y     = height - barH + 2;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={barH} rx={4} fill={d.color || 'var(--nx-accent)'} opacity={0.85} />
            <text x={x + barW / 2} y={height + 14} textAnchor="middle" fontSize={9} fill="var(--nx-text-muted)">{d.label}</text>
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={9} fill="var(--nx-text-muted)">{fmtMoney(d.value)}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ── Sparkline ── */
const RevSparkline = ({ points }) => {
  if (!points || points.length < 2) return null;
  const maxY = Math.max(...points.map(p => p.y), 1);
  const minY = Math.min(...points.map(p => p.y), 0);
  const range = maxY - minY || 1;
  const W = 180, H = 40;
  const px = (i) => (i / (points.length - 1)) * W;
  const py = (y) => H - ((y - minY) / range) * H;
  const d  = points.map((p, i) => (i === 0 ? 'M' : 'L') + px(i).toFixed(1) + ',' + py(p.y).toFixed(1)).join(' ');
  return (
    <svg width={W} height={H + 12} style={{ overflow: 'visible' }}>
      <path d={d} fill="none" stroke="var(--nx-accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={px(i)} cy={py(p.y)} r={2.5} fill="var(--nx-accent)" />
      ))}
      {points.map((p, i) => (
        <text key={i} x={px(i)} y={H + 12} textAnchor="middle" fontSize={8} fill="var(--nx-text-muted)">{p.label}</text>
      ))}
    </svg>
  );
};

/* ── Sortable column header ── */
const SortTh = ({ col, label, sort, onSort }) => (
  <th
    onClick={() => onSort(col)}
    style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', background: 'var(--nx-surface-alt)' }}
  >
    {label} {sort.col === col ? (sort.asc ? '↑' : '↓') : ''}
  </th>
);

/* ══════════════════════════════════════════════════════════════
   REVENUE FORECAST VIEW
   ══════════════════════════════════════════════════════════════ */
const RevenueForecastView = () => {
  const store   = useStore();
  const wsId    = window.__nx_auth?.user?.activeWorkspaceId;

  const [serverData,  setServerData]  = useState(null);
  const [serverError, setServerError] = useState(false);
  const [sort,        setSort]        = useState({ col: 'weighted', asc: false });

  /* Try server forecast first */
  useEffect(() => {
    if (!wsId || !window.__nx_api?.analyticsP3) return;
    window.__nx_api.analyticsP3.getForecast(wsId)
      .then(r => { if (r && Object.keys(r).length) setServerData(r); })
      .catch(() => { setServerError(true); });
  }, [wsId]);

  /* Compute from store */
  const computed = useMemo(() => {
    const pipeline = store.pipeline || [];
    const allDeals = store.deals || [];

    const activeDeals = allDeals.filter(d => {
      const s = pipeline.find(x => x.id === d.stageId);
      return !d.archived && s && s.kind !== 'WON' && s.kind !== 'LOST';
    });
    const wonDeals  = allDeals.filter(d => {
      const s = pipeline.find(x => x.id === d.stageId);
      return s?.kind === 'WON';
    });

    const getProb = (d) => {
      const s = pipeline.find(x => x.id === d.stageId);
      return s ? (s.prob ?? null) : null;
    };

    const weighted  = activeDeals.reduce((sum, d) => sum + (d.value || 0) * getProb(d) / 100, 0);
    const commit    = activeDeals.filter(d => getProb(d) >= 90).reduce((sum, d) => sum + (d.value || 0), 0);
    const bestCase  = activeDeals.filter(d => getProb(d) >= 50).reduce((sum, d) => sum + (d.value || 0), 0);
    const totalPipe = activeDeals.reduce((sum, d) => sum + (d.value || 0), 0);
    const closedWon = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);

    /* By owner */
    const ownerMap = {};
    activeDeals.forEach(d => {
      const o = d.owner || 'Unassigned';
      if (!ownerMap[o]) ownerMap[o] = { owner: o, weighted: 0, commit: 0, bestCase: 0, deals: 0 };
      const p = getProb(d);
      ownerMap[o].weighted  += (d.value || 0) * p / 100;
      ownerMap[o].commit    += p >= 90 ? (d.value || 0) : 0;
      ownerMap[o].bestCase  += p >= 50 ? (d.value || 0) : 0;
      ownerMap[o].deals     += 1;
    });
    const byOwner = Object.values(ownerMap);

    /* Revenue trend: last 6 months by createdAt */
    const now    = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { label: d.toLocaleDateString('en-US', { month: 'short' }), year: d.getFullYear(), month: d.getMonth() };
    });
    const trendPoints = months.map(m => {
      const total = allDeals
        .filter(d => { const cd = new Date(d.createdAt || 0); return cd.getFullYear() === m.year && cd.getMonth() === m.month; })
        .reduce((s, d) => s + (d.value || 0), 0);
      return { label: m.label, y: total };
    });

    /* Deal-level list with weighted */
    const dealList = activeDeals.map(d => {
      const p = getProb(d);
      return {
        ...d,
        prob:     p,
        weighted: p != null ? Math.round((d.value || 0) * p / 100) : null,
        stageName: pipeline.find(s => s.id === d.stageId)?.name || '—',
      };
    });

    return { weighted, commit, bestCase, totalPipe, closedWon, byOwner, trendPoints, dealList, barData: [
      { label: 'Commit',     value: commit,    color: '#10B981' },
      { label: 'Best Case',  value: bestCase,  color: '#0EA5E9' },
      { label: 'Pipeline',   value: totalPipe, color: '#6366f1' },
      { label: 'Closed Won', value: closedWon, color: '#F59E0B' },
    ]};
  }, [store.deals, store.pipeline]);

  /* Merge server data: use server values for KPI cards and byOwner only;
     always use computed values for dealList, trendPoints, and barData */
  const displayData = useMemo(() => {
    const base = computed;
    if (serverData && !serverError) {
      return {
        ...base,
        weighted:  serverData.weighted  ?? base.weighted,
        commit:    serverData.commit    ?? base.commit,
        bestCase:  serverData.bestCase  ?? base.bestCase,
        totalPipe: serverData.pipeline  ?? base.totalPipe,
        closedWon: serverData.closedWon ?? base.closedWon,
        byOwner:   serverData.byOwner?.length ? serverData.byOwner : base.byOwner,
      };
    }
    return base;
  }, [serverData, serverError, computed]);
  const data = displayData;

  const toggleSort = (col) => setSort(s => ({ col, asc: s.col === col ? !s.asc : false }));
  const sortedDeals = useMemo(() => {
    const list = [...(data.dealList || [])];
    list.sort((a, b) => {
      const av = a[sort.col] ?? 0, bv = b[sort.col] ?? 0;
      return sort.asc ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
    });
    return list;
  }, [data.dealList, sort]);

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: 1200, margin: '0 auto' }}>

      {/* Title */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Revenue Forecast</h1>
        <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: '4px 0 0' }}>Pipeline-based projections updated in real time from store data.</p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
        <ForecastStatCard label="Weighted Forecast" value={fmtMoneyFull(data.weighted)}  sub="Value × Probability"     tone="blue" />
        <ForecastStatCard label="Commit"             value={fmtMoneyFull(data.commit)}    sub="≥90% probability"        tone="green" />
        <ForecastStatCard label="Best Case"          value={fmtMoneyFull(data.bestCase)}  sub="≥50% probability"        tone="purple" />
        <ForecastStatCard label="Total Pipeline"     value={fmtMoneyFull(data.totalPipe)} sub="All active deal value"   tone="amber" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 'var(--space-5)' }}>

        {/* Bar chart */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Forecast by Category</div>
          <BarChart data={data.barData || []} height={120} />
        </div>

        {/* Revenue trend */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Pipeline Created — 6 Month Trend</div>
          <RevSparkline points={data.trendPoints || []} />
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 8 }}>Deal value by creation month</div>
        </div>
      </div>

      {/* Manager roll-up */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', marginBottom: 'var(--space-5)', overflowX: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Team Roll-up</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {['Owner', 'Weighted', 'Commit', 'Best Case', '# Deals'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--nx-surface-alt)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.byOwner || []).sort((a, b) => b.weighted - a.weighted).map(row => (
              <tr key={row.owner} style={{ borderTop: '1px solid var(--nx-border)' }}>
                <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Avatar name={row.owner} size={22} tone="var(--color-primary-500)" />
                    <span>{row.owner}</span>
                  </div>
                </td>
                <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--nx-accent)' }}>{fmtMoneyFull(row.weighted)}</td>
                <td style={{ padding: '8px 10px' }}>{fmtMoneyFull(row.commit)}</td>
                <td style={{ padding: '8px 10px' }}>{fmtMoneyFull(row.bestCase)}</td>
                <td style={{ padding: '8px 10px' }}>{row.deals}</td>
              </tr>
            ))}
            {(!data.byOwner || data.byOwner.length === 0) && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No active deal data available.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Deal-level list */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', overflowX: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Deal-Level Forecast</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <SortTh col="title"      label="Deal"          sort={sort} onSort={toggleSort} />
              <SortTh col="owner"      label="Owner"         sort={sort} onSort={toggleSort} />
              <SortTh col="value"      label="Value"         sort={sort} onSort={toggleSort} />
              <SortTh col="prob"       label="Prob %"        sort={sort} onSort={toggleSort} />
              <SortTh col="stageName"  label="Stage"         sort={sort} onSort={toggleSort} />
              <SortTh col="weighted"   label="Weighted"      sort={sort} onSort={toggleSort} />
              <SortTh col="closeDate"  label="Expected Close" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedDeals.slice(0, 50).map(d => (
              <tr key={d.id} style={{ borderTop: '1px solid var(--nx-border)', cursor: 'pointer' }} onClick={() => window.__nx_store?.openDrawer && window.__nx_store.openDrawer(d)}>
                <td style={{ padding: '8px 10px', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</td>
                <td style={{ padding: '8px 10px' }}>{d.owner || '—'}</td>
                <td style={{ padding: '8px 10px' }}>{fmtMoney(d.value)}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 32, height: 4, background: 'var(--nx-border)', borderRadius: 2 }}>
                      <div style={{ width: (d.prob ?? 0) + '%', height: '100%', background: (d.prob ?? 0) >= 70 ? 'var(--nx-success)' : (d.prob ?? 0) >= 40 ? '#F59E0B' : 'var(--nx-danger)', borderRadius: 2 }} />
                    </div>
                    <span>{d.prob != null ? d.prob + '%' : '—'}</span>
                  </div>
                </td>
                <td style={{ padding: '8px 10px' }}><Pill tone="blue">{d.stageName}</Pill></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--nx-accent)' }}>{fmtMoney(d.weighted)}</td>
                <td style={{ padding: '8px 10px', color: 'var(--nx-text-muted)' }}>{d.closeDate || '—'}</td>
              </tr>
            ))}
            {sortedDeals.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No active deals in pipeline.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
window.RevenueForecastView = RevenueForecastView;
